import {
  GalleryThumbnailsIcon,
  ScanTextIcon,
  TelescopeIcon,
} from 'lucide-animated';
import { useReducedMotion } from 'motion/react';
import {
  type ForwardRefExoticComponent,
  type RefAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';

// Each animated icon exposes imperative startAnimation()/stopAnimation().
interface AnimatedIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

type AnimatedLucideIcon = ForwardRefExoticComponent<
  { size?: number; className?: string } & RefAttributes<AnimatedIconHandle>
>;

const LEARNING_ICONS: AnimatedLucideIcon[] = [
  GalleryThumbnailsIcon,
  ScanTextIcon,
  TelescopeIcon,
];
const ITERATIONS_PER_ICON = 3;
// Long enough for the longest icon animation (~0.9s) to finish.
const PLAY_DURATION_MS = 1000;
const GAP_BETWEEN_ITERATIONS_MS = 250;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

interface LearningIconCyclerProps {
  size?: number;
  className?: string;
}

// Plays each animated icon a few times, then advances; static under reduced-motion.
export const LearningIconCycler = ({
  size = 20,
  className,
}: LearningIconCyclerProps) => {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const iconRef = useRef<AnimatedIconHandle>(null);

  useEffect(() => {
    if (reduceMotion) return;
    let cancelled = false;

    const run = async () => {
      for (let i = 0; i < ITERATIONS_PER_ICON; i += 1) {
        if (cancelled) return;
        iconRef.current?.startAnimation();
        await sleep(PLAY_DURATION_MS);
        if (cancelled) return;
        iconRef.current?.stopAnimation();
        await sleep(GAP_BETWEEN_ITERATIONS_MS);
      }
      if (!cancelled) {
        setIndex((prev) => (prev + 1) % LEARNING_ICONS.length);
      }
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [index, reduceMotion]);

  if (reduceMotion) {
    return (
      <TelescopeIcon size={size} className={className} animateOnHover={false} />
    );
  }

  const Icon = LEARNING_ICONS[index];
  return <Icon ref={iconRef} size={size} className={className} />;
};
